import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, Eye, EyeOff, ImagePlus } from "lucide-react";
import RichTextEditor from "@/components/admin/RichTextEditor";
import MediaPicker from "@/pages/admin/MediaPicker";

const empty = { title: "", slug: "", date: new Date().toISOString().slice(0, 10), excerpt: "", body: "", image_url: "", is_published: true, tags: [] };

export default function AdminNews() {
  const { authedClient } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedClient().get("/admin/news");
      setPosts(data);
    } catch { toast.error("Could not load news posts"); } finally { setLoading(false); }
  }, [authedClient]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm({ ...empty }); setEditing("new"); };
  const startEdit = (p) => { setForm({ ...empty, ...p, tags: p.tags || [] }); setEditing(p.id); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      const payload = { ...form, tags: (typeof form.tags === "string" ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : form.tags) };
      if (editing === "new") {
        const { data } = await authedClient().post("/admin/news", payload);
        setPosts([data, ...posts]);
        toast.success("Posted");
      } else {
        const { data } = await authedClient().patch(`/admin/news/${editing}`, payload);
        setPosts(posts.map(p => p.id === editing ? data : p));
        toast.success("Saved");
      }
      setEditing(null); setForm(empty);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const togglePublished = async (p) => {
    try {
      const { data } = await authedClient().patch(`/admin/news/${p.id}`, { is_published: !p.is_published });
      setPosts(posts.map(x => x.id === p.id ? data : x));
    } catch { toast.error("Could not toggle"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await authedClient().delete(`/admin/news/${p.id}`);
      setPosts(posts.filter(x => x.id !== p.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-news-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="eyebrow mb-2">★ CMS</p>
          <h1 className="font-display text-3xl text-[color:var(--blue-ink)]">News & Announcements</h1>
          <p className="text-sm text-[color:var(--ink-soft)] mt-1">
            Longer-form posts shown on the public news page. Great for High Holy Days messages,
            welcome letters, obituaries, and community milestones.
          </p>
        </div>
        {!editing && (
          <button onClick={startNew} className="btn-primary" data-testid="admin-news-new">
            <Plus className="w-4 h-4" /> New post
          </button>
        )}
      </div>

      {editing && (
        <form onSubmit={save} className="bg-[color:var(--cream)] rounded-2xl p-6 mb-8 space-y-4" data-testid="admin-news-form">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl text-[color:var(--blue-ink)]">{editing === "new" ? "New" : "Edit"} post</h2>
            <button type="button" onClick={() => { setEditing(null); setForm(empty); }}><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Title *"><input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} className={ic} data-testid="news-form-title" /></Field>
            <Field label="Date"><input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className={ic} /></Field>
            <Field label="Slug (URL)"><input value={form.slug || ""} onChange={e => setForm({...form, slug: e.target.value})} className={ic} placeholder="auto from title" /></Field>
            <Field label="Tags (comma-separated)"><input value={typeof form.tags === "string" ? form.tags : (form.tags || []).join(", ")} onChange={e => setForm({...form, tags: e.target.value})} className={ic} placeholder="holidays, rabbi" /></Field>
          </div>
          <Field label="Excerpt (1-2 sentences shown in the list)">
            <textarea rows={2} value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} className={ic} />
          </Field>
          <Field label="Featured image">
            <div className="flex items-center gap-3">
              {form.image_url ? (
                <img src={form.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.image_url}` : form.image_url}
                     alt="" className="w-24 h-24 object-cover rounded-lg border border-[color:var(--line)]" />
              ) : (
                <div className="w-24 h-24 rounded-lg border border-dashed border-[color:var(--line)] flex items-center justify-center text-[color:var(--muted)]">
                  <ImagePlus className="w-5 h-5" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input value={form.image_url || ""} onChange={e => setForm({...form, image_url: e.target.value})} className={ic} placeholder="/api/files/... or full URL" />
                <div className="flex gap-2">
                  <button type="button" onClick={() => setPickerOpen(true)} className="btn-ghost text-sm py-2 px-3">Pick from library</button>
                  {form.image_url && <button type="button" onClick={() => setForm({...form, image_url: ""})} className="text-sm text-red-600 hover:underline">Remove</button>}
                </div>
              </div>
            </div>
          </Field>
          <Field label="Body">
            <RichTextEditor value={form.body} onChange={(html) => setForm(f => ({...f, body: html}))} />
          </Field>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_published} onChange={e => setForm({...form, is_published: e.target.checked})} className="accent-[color:var(--blue)]" />
            Published (visible on the public news page)
          </label>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" data-testid="news-form-save"><Save className="w-4 h-4" /> Save</button>
            <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="btn-ghost">Cancel</button>
          </div>
        </form>
      )}

      {loading ? <p className="text-center text-[color:var(--muted)] py-8">Loading…</p>
        : posts.length === 0 ? <p className="text-center text-[color:var(--muted)] py-8">No news posts yet.</p>
        : (
          <div className="space-y-3">
            {posts.map(p => (
              <div key={p.id} className={`p-4 rounded-xl border ${p.is_published ? "border-[color:var(--line)] bg-white" : "border-dashed border-[color:var(--line)] bg-[color:var(--cream)]/50 opacity-70"}`} data-testid={`admin-news-row-${p.id}`}>
                <div className="flex items-start gap-3">
                  {p.image_url && <img src={p.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p.image_url}` : p.image_url} alt="" className="w-16 h-16 object-cover rounded-lg" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[color:var(--blue-ink)]">{p.title}</div>
                    <div className="text-xs text-[color:var(--muted)] mt-0.5">{p.date} · /news/{p.slug}</div>
                    <div className="text-sm text-[color:var(--ink-soft)] mt-1 line-clamp-1">{p.excerpt || "-"}</div>
                  </div>
                  <div className="flex items-start gap-1">
                    <button onClick={() => togglePublished(p)} className="p-1.5 rounded hover:bg-[color:var(--cream)]" title={p.is_published ? "Published" : "Draft"}>
                      {p.is_published ? <Eye className="w-4 h-4 text-[color:var(--blue-deep)]" /> : <EyeOff className="w-4 h-4 text-[color:var(--muted)]" />}
                    </button>
                    <button onClick={() => startEdit(p)} className="p-1.5 rounded hover:bg-[color:var(--cream)]"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(p)} className="p-1.5 rounded text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      <MediaPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={(a) => setForm(f => ({...f, image_url: a.public_url}))} />
    </div>
  );
}

const ic = "w-full rounded-lg border border-[color:var(--line)] px-3 py-2 bg-white focus:outline-none focus:border-[color:var(--blue)]";
function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-[color:var(--ink-soft)] mb-1">{label}</label>{children}</div>;
}
